// LES EMOTES (26/07, demande Claude-Alain : « Emote, danse, emoji »).
//
// Une emote, c'est deux choses : une BULLE au-dessus de la tête, et un GESTE du corps. Le geste
// réutilise les poses existantes du Baroudeur — on ne redessine pas le héros, on le fait bouger.
// C'est ce qui permet d'en avoir huit tout de suite plutôt qu'une seule, très bien animée.
//
// Module PUR : ni DOM, ni canvas, ni simulation. L'emote est 100 % locale et cosmétique — elle
// n'entre ni dans le hash d'état, ni dans le rejeu réseau. Elle ne peut donc pas désynchroniser
// une partie, et un joueur qui danse ne gêne personne.

/** Comment le CORPS bouge pendant l'emote. Le dessin, lui, ne change pas. */
export type EmoteMove =
  | 'calme'    // rien : la bulle parle toute seule
  | 'saut'     // petits bonds sur place
  | 'danse'    // bascule gauche-droite + ondulation
  | 'tremble'  // secousses rapides (le fou rire)
  | 'salut'    // une inclinaison tenue, puis retour

export interface Emote {
  id: string
  /** Ce qui s'affiche dans la bulle. */
  emoji: string
  /** Le nom au menu. */
  name: string
  move: EmoteMove
}

/** Durée d'une emote, en millisecondes. Assez long pour être vu, assez court pour ne pas gêner. */
export const EMOTE_MS = 1900

// Huit emotes : de quoi dire bonjour, féliciter, se moquer gentiment et faire le clown. L'ordre
// est celui du menu, et il est STABLE : les touches 1 à 8 le suivent.
export const EMOTES: readonly Emote[] = [
  { id: 'salut', emoji: '👋', name: 'Salut', move: 'salut' },
  { id: 'bravo', emoji: '👏', name: 'Bravo', move: 'saut' },
  { id: 'danse', emoji: '🕺', name: 'Danse', move: 'danse' },
  { id: 'rire', emoji: '😂', name: 'Fou rire', move: 'tremble' },
  { id: 'coeur', emoji: '❤️', name: 'Cœur', move: 'calme' },
  { id: 'pense', emoji: '🤔', name: 'Je réfléchis', move: 'calme' },
  { id: 'fete', emoji: '🎉', name: 'La fête', move: 'saut' },
  { id: 'oups', emoji: '😱', name: 'Oups', move: 'tremble' },
]

const PAR_ID = new Map(EMOTES.map((e) => [e.id, e]))
export function emoteById(id: string): Emote | undefined { return PAR_ID.get(id) }

/** Ce qu'il faut dessiner à l'instant `t` (ms depuis le début de l'emote). */
export interface EmoteFrame {
  /** Décalage vertical du corps, en fraction de case (négatif = vers le haut). */
  bob: number
  /** Inclinaison du corps, en radians. */
  tilt: number
  /** Opacité de la bulle, 0..1 (elle apparaît vite et s'efface à la fin). */
  bulle: number
  /** Montée de la bulle, en fraction de case. */
  monte: number
  /** Grossissement de la bulle à l'apparition, 0..1 (1 = taille normale). */
  pop: number
}

/**
 * Anime l'emote : PURE fonction du temps écoulé. Hors de [0, EMOTE_MS], tout est à zéro — un
 * appelant qui oublie d'arrêter l'emote obtient donc simplement un héros normal, jamais un
 * héros figé de travers.
 */
export function emoteFrame(move: EmoteMove, elapsedMs: number): EmoteFrame {
  if (elapsedMs < 0 || elapsedMs >= EMOTE_MS) return { bob: 0, tilt: 0, bulle: 0, monte: 0, pop: 0 }
  const k = elapsedMs / EMOTE_MS          // avancement 0..1
  const s = elapsedMs / 1000              // secondes, pour les oscillations
  // La bulle : apparaît en 120 ms, tient, puis s'efface sur le dernier quart.
  const bulle = Math.min(1, elapsedMs / 120) * (k > 0.75 ? (1 - k) / 0.25 : 1)
  const pop = Math.min(1, elapsedMs / 160)
  const monte = 0.10 + k * 0.22
  let bob = 0, tilt = 0
  switch (move) {
    case 'saut':
      // deux bonds nets : |sin| donne le rebond, et l'amplitude retombe à la fin
      bob = -Math.abs(Math.sin(s * Math.PI * 2.6)) * 0.20 * (1 - k * 0.35)
      break
    case 'danse':
      bob = -Math.abs(Math.sin(s * Math.PI * 3.4)) * 0.11
      tilt = Math.sin(s * Math.PI * 3.4) * 0.22
      break
    case 'tremble':
      bob = Math.sin(s * 34) * 0.035
      tilt = Math.sin(s * 27) * 0.06
      break
    case 'salut':
      // une inclinaison qui part vite, tient, et revient : le geste de la main levée
      tilt = -Math.sin(Math.min(1, k * 1.6) * Math.PI) * 0.17
      break
    case 'calme':
      break
  }
  return { bob, tilt, bulle, monte, pop }
}
